import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarRef,
} from '@angular/material/snack-bar';
import { BehaviorSubject } from 'rxjs';

import { LoadingDialogComponent } from '../components/loading-dialog';
import { SnackbarComponent, SnackbarData } from '../components/snackbar';
import { Connection } from '@lockton-apps/shared/util/helpers';

const snackbarPanelClassMap = new Map<SubtleMessageType, string>([
  ['success', 'snackbar-success'],
  ['error', 'snackbar-error'],
  ['info', 'snackbar-info'],
]);

export type SubtleMessageData = MatSnackBarConfig<
  Omit<SnackbarData, 'message'>
> & { type?: SubtleMessageType };

type SubtleMessageType = 'success' | 'error' | 'info';

export const DEFAULT_SUBTLE_MESSAGE_OPTIONS = {
  type: 'info',
  duration: 6000,
};

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  loadingOverlaySubject = new BehaviorSubject(false);
  loadingOverlay$ = this.loadingOverlaySubject.asObservable();

  dialogRef!: MatDialogRef<unknown>;

  private _messageIconMap = new Map<SubtleMessageType, string>([
    ['success', 'tick'],
    ['error', 'close'],
    ['info', 'i'],
  ]);

  constructor(private snackbar: MatSnackBar, private dialog: MatDialog) {}

  showLoadingOverlay(): void {
    this.loadingOverlaySubject.next(true);
  }

  hideLoadingOverlay(): void {
    this.loadingOverlaySubject.next(false);
  }

  showLoadingDialog(warningText: string): void {
    if (!this.dialogRef) {
      this.dialogRef = this.dialog.open(LoadingDialogComponent, {
        data: {
          warningText,
        },
        maxWidth: 400,
        disableClose: true,
      });
    }
  }

  hideLoadingDialog(): void {
    this.dialogRef && this.dialogRef.close();
  }

  subtleMessage(
    message: string,
    sd: SubtleMessageData = {}
  ): MatSnackBarRef<unknown> {
    const { data, type = 'info', ...matData } = sd;

    const passedMatData = {
      ...DEFAULT_SUBTLE_MESSAGE_OPTIONS,
      ...matData,
    };

    return this.snackbar.openFromComponent<SnackbarComponent, SnackbarData>(
      SnackbarComponent,
      {
        data: {
          ...data,
          message,
          icon: Connection.isOnline
            ? this._getSubtleMessageIcon(type)
            : undefined,
        },
        ...passedMatData,
        panelClass: this._getSubtleMessagePanelClass(
          type,
          passedMatData.panelClass
        ),
      }
    );
  }

  private _getSubtleMessageIcon(type: SubtleMessageType): string {
    return this._messageIconMap.get(type) || '';
  }

  private _getSubtleMessagePanelClass(
    type: SubtleMessageType,
    additionalPanelClass: string | string[] | undefined
  ): string | string[] {
    let panelClass: string | string[];

    if (!additionalPanelClass) {
      panelClass = snackbarPanelClassMap.get(type) || 'info';
    } else {
      panelClass = Array.isArray(additionalPanelClass)
        ? [...additionalPanelClass, snackbarPanelClassMap.get(type) || 'info']
        : [additionalPanelClass];
    }
    return panelClass;
  }
}
